import type { FC, PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  IKeycloakBasedAuth,
  LoginResponse,
  SamlLookupResponse,
} from './type';
import { AuthenticationError, RequiredActions } from './type';
import type { TokenPayload } from '@pflegenavi/shared/utils';
import { getTokenPayloadFromToken } from '@pflegenavi/shared/utils';
import { getFetchOptions } from './utils/getFetchOptions';

// App state types
export type AppStateStatus =
  | 'active'
  | 'background'
  | 'inactive'
  | 'unknown'
  | 'extension';

// App state provider interface
export interface AppStateProvider {
  getCurrentState(): AppStateStatus;
  addStateChangeListener(listener: (state: AppStateStatus) => void): () => void;
}

// Network connectivity provider interface
export interface NetworkInfoProvider {
  isConnected(): Promise<boolean>;
  addConnectionListener(listener: (isConnected: boolean) => void): () => void;
}

// Error reporting interface (e.g., for Sentry)
export interface ErrorReporter {
  captureException(error: Error, context?: Record<string, any>): void;
  captureMessage(message: string, context?: Record<string, any>): void;
}

// Login method response type
export interface LoginMethodResponse {
  loginType: 'PASSWORD' | 'REDIRECT';
  redirectUrl: string | null;
}

interface LogoutResponseData {
  logout_type?: 'local' | 'saml';
  saml_logout_url?: string;
  post_logout_redirect_url?: string;
}

function setApplicationHeaders(
  options?: RequestInit & { headers?: Headers }
): RequestInit & { headers?: Headers } {
  // @ts-expect-error //global is untyped
  const application = window.global.pflegenaviApplication;
  // @ts-expect-error //global is untyped
  const applicationVersion = window.global.pflegenaviApplicationVersion;
  // @ts-expect-error //global is untyped
  const tenant = window.global.tenant;
  const headers = options?.headers ?? new Headers();
  headers.set('X-PN-Application', application);
  headers.set('X-PN-Application-Version', applicationVersion);
  if (application === 'service-provider-web') {
    headers.set('X-PN-StripeMode', 'live');
  }
  if (tenant) {
    headers.set('X-PN-Tenant', tenant);
  }
  const optionsWithApplication = {
    ...options,
    headers,
  };

  return optionsWithApplication;
}

const Context = createContext<IKeycloakBasedAuth | undefined>(undefined);

interface Storage {
  setItem: (key: string, value: string) => Promise<void> | void;
  getItem: (key: string) => Promise<string | null> | string | null;

  removeItem(key: string): void;
}

// Global singleton instance storage
let authenticationInstance: PflegenaviAuthentication | null = null;

interface PflegenaviAuthenticationProviderProps {
  storage: Storage;
  getWelcomeToken: () => Promise<string | null>;
  shouldUseNewLogin: boolean;
  apiUrl: string;
  redirectIfNecessary: (url: string) => 'redirected' | undefined;
  /**
   * Allow to register cleanup functions when the user logs out.
   */
  onLogout: () => void;
  /**
   * Optional network info provider for handling online/offline scenarios
   */
  networkInfoProvider?: NetworkInfoProvider;
  /**
   * Optional error reporter for capturing authentication errors (e.g., Sentry)
   */
  errorReporter?: ErrorReporter;
  /**
   * Optional app state provider for tracking foreground/background state
   */
  appStateProvider?: AppStateProvider;
  /**
   * Optional auth instance for dependency injection
   * If provided, this instance will be used instead of creating a new one
   */
  authInstance?: PflegenaviAuthentication;
}

const REFRESH_TOKEN = 'REFRESH_TOKEN';

export const PflegenaviAuthenticationProvider: FC<
  PropsWithChildren<PflegenaviAuthenticationProviderProps>
> = ({
  children,
  storage,
  getWelcomeToken,
  shouldUseNewLogin,
  apiUrl,
  redirectIfNecessary,
  onLogout,
  networkInfoProvider,
  errorReporter,
  appStateProvider,
  authInstance: providedAuthInstance,
}) => {
  const [, rerender] = useState({});
  const isMountedRef = useRef(true);

  // Safe rerender that only updates if component is mounted
  const safeRerender = useCallback(() => {
    if (isMountedRef.current) {
      rerender({});
    } else {
      // Only report the warning when rerender is attempted on unmounted component
      errorReporter?.captureMessage(
        'Authentication rerender attempted on unmounted component',
        {
          tags: {
            source: 'auth_flow',
            event: 'unmounted_rerender',
          },
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const auth = useMemo(() => {
    // If auth instance was provided via dependency injection, use it
    if (providedAuthInstance) {
      // Wire up the rerender callback so auth state changes trigger component rerenders
      providedAuthInstance.updateRerenderCallback(safeRerender);
      return providedAuthInstance;
    }

    // Otherwise, create singleton instance if it doesn't exist
    if (!authenticationInstance) {
      authenticationInstance = new PflegenaviAuthentication(
        apiUrl,
        safeRerender,
        storage,
        redirectIfNecessary,
        onLogout,
        networkInfoProvider,
        errorReporter,
        appStateProvider
      );
    } else {
      // Update the rerender callback for the existing instance
      authenticationInstance.updateRerenderCallback(safeRerender);
    }

    return authenticationInstance;
    // On mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providedAuthInstance]);

  // Track mounted state
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      // Clear the rerender callback to prevent memory leaks
      // Use the actual auth instance (which could be providedAuthInstance or singleton)
      auth.clearRerenderCallback();
    };
  }, [auth]);

  // TODO ensure initialization can be multiple times for each token
  useEffect(() => {
    // Only initialize if we created the instance (not provided via DI)
    if (!providedAuthInstance && auth instanceof PflegenaviAuthentication) {
      void auth.initialize(getWelcomeToken);
      return () => {
        auth.cleanup();
      };
    }
    return undefined;
  }, [auth, getWelcomeToken, providedAuthInstance]);

  if (!shouldUseNewLogin) {
    return children;
  }

  // Extract the IKeycloakBasedAuth interface from the instance
  const contextValue = providedAuthInstance
    ? providedAuthInstance.context()
    : (auth as PflegenaviAuthentication).context();

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

export function useAuthentication<T extends boolean>(
  shouldUseNewLogin: T
): T extends true ? IKeycloakBasedAuth : IKeycloakBasedAuth | undefined {
  const auth = useContext(Context);
  if (!auth && shouldUseNewLogin) {
    throw new Error('No authentication provider found');
  }
  // @ts-expect-error // can't type due to dependency on shouldUseNewLogin
  return auth;
}

export class PflegenaviAuthentication {
  private logger: undefined | typeof console = undefined;
  private access_token: string | undefined;

  private required_actions: RequiredActions[] = [];
  private payment_processor: 'stripe' | 'mangopay' | undefined;
  private required_action_token: string | undefined;

  private error: string | undefined;
  private errorOptions:
    | {
        invitationWasValidFor?: string;
      }
    | undefined;
  private redirectUri: string | undefined;

  private initializing = true;

  private refreshTimer: ReturnType<typeof setTimeout> | undefined = undefined;
  private retryCount = 0;
  private maxRetries = 5;
  private isNetworkConnected = true;
  private connectionListenerCleanup: (() => void) | undefined;
  private appStateListenerCleanup: (() => void) | undefined;
  private appState: AppStateStatus = 'active';

  // Track the last 10 refresh tokens used
  private usedRefreshTokens: string[] = [];
  private maxTrackedTokens = 10;

  /**
   Promise to avoid running multiple operations at the same time.
   */
  private promise: any;

  private async waitForForeground(): Promise<boolean> {
    // If no app state provider, assume we're in foreground (web environment)
    if (!this.appStateProvider) {
      return true;
    }

    const maxRetries = 5;
    const retryDelay = 2000; // 2 seconds

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      if (this.appState === 'active') {
        return true;
      }

      this.logger?.log(
        `App is in background, waiting... (attempt ${
          attempt + 1
        }/${maxRetries})`
      );
      await new Promise((resolve) => setTimeout(resolve, retryDelay));
    }

    // Still in background after all retries
    this.errorReporter?.captureMessage(
      'Token refresh blocked: App remained in background after retries',
      {
        tags: {
          source: 'auth_flow',
          event: 'background_token_refresh_blocked',
        },
        extra: {
          retries: maxRetries,
          appState: this.appState,
        },
      }
    );

    return false;
  }

  constructor(
    private baseUrl: string,
    private rerender: () => void,
    private storage: Storage,
    private redirectIfNecessary: (hostname: string) => 'redirected' | undefined,
    private onLogout: () => void,
    private networkInfoProvider?: NetworkInfoProvider,
    private errorReporter?: ErrorReporter,
    private appStateProvider?: AppStateProvider
  ) {
    // Initialize app state if provider is available
    if (this.appStateProvider) {
      this.appState = this.appStateProvider.getCurrentState();
    }
  }

  updateRerenderCallback(newRerender: () => void): void {
    this.rerender = newRerender;
  }

  clearRerenderCallback(): void {
    this.rerender = () => {
      // No-op: callback has been cleared
    };
  }

  private getHeaders(otherHeaders: Headers) {
    const headers =
      setApplicationHeaders({
        headers: otherHeaders,
      }).headers ?? new Headers();

    if (this.access_token) {
      headers.set('Authorization', `Bearer ${this.access_token}`);
    }

    return headers;
  }

  private clearTimer() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = undefined;
    }
  }

  private setError(
    error: string,
    options?: {
      [key: string]: string;
    }
  ) {
    this.logger?.info('Error', error);
    this.access_token = undefined;
    this.required_actions = [];
    this.required_action_token = undefined;
    this.error = error;
    this.errorOptions = options;
    this.redirectUri = undefined;
    this.clearTimer();
    this.storage.removeItem(REFRESH_TOKEN);
  }

  private setRequiredActions(
    actions: { rqac: RequiredActions[]; pp: 'stripe' | 'mangopay' },
    token: string
  ) {
    this.access_token = undefined;
    this.required_actions = actions.rqac;
    this.payment_processor = actions.pp;
    this.required_action_token = token;
    this.error = undefined;
    this.redirectUri = undefined;
    this.storage.removeItem(REFRESH_TOKEN);
    this.clearTimer();
  }

  private setTokens(
    accessToken: string,
    refreshToken: string,
    redirectUri: string | undefined,
    refreshInSeconds: number,
    paymentProcessor: 'stripe' | 'mangopay'
  ) {
    this.access_token = accessToken;
    this.required_actions = [];
    this.required_action_token = undefined;
    this.payment_processor = paymentProcessor;
    this.error = undefined;
    this.redirectUri = redirectUri;
    this.storage.setItem(REFRESH_TOKEN, refreshToken);

    this.clearTimer();
    this.refreshTimer = setTimeout(
      this.updateToken.bind(this),
      refreshInSeconds * 1000
    );
  }

  private reset() {
    this.onLogout();
    this.access_token = undefined;
    this.required_actions = [];
    this.required_action_token = undefined;
    this.error = undefined;
    this.redirectUri = undefined;
    this.storage.removeItem(REFRESH_TOKEN);
    this.clearTimer();
  }

  private async handleUnauthorizedResponse(result: Response): Promise<boolean> {
    try {
      const jsonResponse = await result.json();
      if (
        jsonResponse.errors?.type === 'saml_reauthentication_required' &&
        jsonResponse.errors?.redirect_url
      ) {
        // Attempt to redirect to SAML provider
        const redirected = this.redirectIfNecessary(
          jsonResponse.errors.redirect_url
        );

        if (redirected === 'redirected') {
          // Successfully redirected - state will be lost anyway
          return true;
        }

        // Redirect failed - log out the user
        this.errorReporter?.captureMessage(
          'Authentication failed: SAML redirect failed',
          {
            tags: { source: 'auth_flow', event: 'saml_redirect_failed' },
          }
        );
        await this.logoutInner({});
        return false;
      }

      // 401 but not SAML re-authentication, log out the user
      this.errorReporter?.captureMessage(
        'Authentication failed: Unauthorized',
        {
          tags: { source: 'auth_flow', event: 'token_refresh_unauthorized' },
          extra: {
            status: result.status,
          },
        }
      );
      await this.logoutInner({});
      return false;
    } catch (error) {
      this.logger?.error('Failed to parse 401 response:', error);
      // On parse error, log out the user
      await this.logoutInner({});
      return false;
    }
  }

  initialize(getWelcomeToken: () => Promise<string | null>): Promise<any> {
    // Set up AppState listener to track foreground/background state if provider is available
    if (this.appStateProvider) {
      this.appStateListenerCleanup =
        this.appStateProvider.addStateChangeListener((nextAppState) => {
          this.appState = nextAppState;

          // If app comes to foreground and we have a token, check if we need to refresh
          if (
            nextAppState === 'active' &&
            this.access_token &&
            !this.refreshTimer
          ) {
            // Schedule a token refresh check
            void this.updateToken();
          }
        });
    }

    // Set up network connectivity listener if provider is available
    if (this.networkInfoProvider) {
      // Initialize network status
      void this.networkInfoProvider.isConnected().then((connected) => {
        this.isNetworkConnected = connected;
      });

      // Set up listener for network changes
      this.connectionListenerCleanup =
        this.networkInfoProvider.addConnectionListener((connected) => {
          const wasDisconnected = !this.isNetworkConnected;
          this.isNetworkConnected = connected;

          // If we've regained connectivity and we have a pending token refresh,
          // attempt to refresh the token now
          if (connected && wasDisconnected && this.access_token) {
            // Clear existing timer and try immediately
            this.clearTimer();
            void this.updateToken();
          }
        });
    }

    return this.waitWithPromise(
      this.initializeInner.bind(this, getWelcomeToken)
    );
  }

  async initializeInner(
    getWelcomeToken: () => Promise<string | null>
  ): Promise<void> {
    this.initializing = true;
    this.rerender();

    const token = await getWelcomeToken();

    if (token) {
      // Check if this welcome token is for the same user as currently authenticated
      const welcomeUserId = getTokenPayloadFromToken(token)?.sub;
      const currentUserId = this.access_token
        ? getTokenPayloadFromToken(this.access_token)?.sub
        : null;

      // Only reset if it's a different user
      if (currentUserId && welcomeUserId && currentUserId === welcomeUserId) {
        // Don't reset - keep the existing token to avoid "No token available" errors
        // This allows service providers to go through onboarding for new nursing homes
        // while staying logged in
      } else if (
        currentUserId &&
        welcomeUserId &&
        currentUserId !== welcomeUserId
      ) {
        this.logger?.info(
          `Existing user ${currentUserId} does not equal deep link user ${welcomeUserId}. Logging out`
        );
        this.reset();
      } else if (!currentUserId && welcomeUserId) {
        // No current user but welcome token has a user - reset and proceed with welcome flow
        //
        this.logger?.info(
          `No existing user but welcome user ${welcomeUserId}. Resetting`
        );
        this.reset();
      } else {
        // No welcome user ID - don't reset if we have an existing session
        // This prevents clearing auth when initialize() is called without a welcome token
      }

      const payload = getTokenPayloadFromToken<
        {
          rqac: RequiredActions[];
          pp: 'stripe' | 'mangopay';
        } & TokenPayload
      >(token);

      if (!payload || payload.exp * 1000 < new Date().valueOf()) {
        if (payload?.exp && payload?.iat) {
          const invitationWasValidFor =
            (payload.exp - payload.iat) / 60 / 60 / 24; // From seconds to days
          this.setError(AuthenticationError.InvitationExpired, {
            invitationWasValidFor: invitationWasValidFor.toString(),
          });
        }
      } else if (payload.typ === 'refresh_token') {
        this.logger?.info('Refresh token - updating');
        await this.updateTokenInner(token);
      } else {
        this.logger?.info('Valid welcome token', payload.rqac);
        const rqac = payload.rqac ?? [];
        this.setRequiredActions(
          {
            rqac: rqac,
            pp: payload.pp,
          },
          token
        );

        if (
          payload.typ === 'verify_email' &&
          this.required_actions.includes(RequiredActions.VERIFY_EMAIL)
        ) {
          this.logger?.info('Verifying email');
          await this.verifyEmail();
        } else {
          // Refresh the token
          await this.loginWithToken();
        }
      }
    } else {
      if (window?.location?.search?.includes('impersonate')) {
        // Clear existing auth state and query cache before impersonation
        // to prevent stale data from the previously logged-in user
        this.reset();
        await this.loginWithToken();
      } else {
        const refreshToken = await this.storage.getItem(REFRESH_TOKEN);
        this.logger?.info('Retrieved refresh token', refreshToken);

        if (refreshToken) {
          this.logger?.info('Refreshing token');
          await this.updateTokenInner(refreshToken);
        }
      }
    }

    this.initializing = false;
    this.rerender();
  }

  private loginUrl() {
    return `${this.baseUrl}/api/users/log_in`;
  }

  private loginMethodUrl() {
    return `${this.baseUrl}/api/users/log_in_method`;
  }

  private samlLookupUrl() {
    return `${this.baseUrl}/api/saml/lookup`;
  }

  private logoutUrl(redirectUrl?: string) {
    const url = new URL('/api/users/log_out', this.baseUrl);
    if (redirectUrl) {
      url.searchParams.set('redirect_url', redirectUrl);
    }
    return url.toString();
  }

  private resetPasswordUrl() {
    return `${this.baseUrl}/api/users/reset_password`;
  }

  private verifyEmailUrl() {
    return `${this.baseUrl}/api/users/verify_email`;
  }

  private acceptTermsAndConditionsUrl() {
    return `${this.baseUrl}/api/users/terms_and_conditions`;
  }

  private refreshTokenUrl() {
    return `${this.baseUrl}/api/users/token/refresh`;
  }

  context(): IKeycloakBasedAuth {
    return {
      login: this.login.bind(this),
      logout: this.logout.bind(this),
      loadUserProfile: this.loadUserProfile.bind(this),
      loginWithToken: this.loginWithToken.bind(this),
      updateToken: this.updateToken.bind(this, undefined),
      getTokenImmediate: this.getTokenImmediate.bind(this),
      token: this.access_token,
      paymentProcessor: this.payment_processor,
      resetPassword: this.resetPassword.bind(this),
      resetPasswordConfirm: this.resetPasswordConfirm.bind(this),
      error: this.error,
      errorOptions: this.errorOptions,
      requiredActions: this.required_actions,
      redirectUri: this.redirectUri,
      initializing: this.initializing,
      acceptTermsAndConditions: this.acceptTermsAndConditions.bind(this),
      cleanup: this.cleanup.bind(this),
      loginMethod: this.loginMethod.bind(this),
      samlLookup: this.samlLookup.bind(this),
    };
  }

  async handleLoginResponse(
    result: Response
  ): Promise<LoginResponse | undefined> {
    if (result.status === 200) {
      const json = await result.json();
      const token = json.data.required_action_token;
      if (token) {
        const payload = getTokenPayloadFromToken<
          {
            rqac: RequiredActions[];
            pp: 'stripe' | 'mangopay';
          } & TokenPayload
        >(token);
        if (!payload) {
          return {
            status: 'error',
            errorMessage: 'Invalid token',
          };
        }

        this.setRequiredActions(payload, token);

        return {
          required_actions: payload.rqac as RequiredActions[],
        };
      } else {
        const payload = getTokenPayloadFromToken(json.data.access_token);

        const refreshInSeconds = !payload?.exp
          ? 600
          : (payload.exp * 1000 - new Date().valueOf()) / 1000 - 10;

        if (payload?.reduri) {
          const redirected = this.redirectIfNecessary(
            `${payload.reduri}?key=${json.data.refresh_token}`
          );
          if (redirected) {
            return { status: 'OK' };
          }
        }

        this.setTokens(
          json.data.access_token,
          json.data.refresh_token,
          payload?.reduri,
          refreshInSeconds,
          payload?.pp ?? 'stripe'
        );

        return { status: 'OK' };
      }
    }
    return undefined;
  }

  /**
   * Runs a function if no other promised function is currently running
   */
  async waitWithPromise<T>(callback: () => Promise<T>): Promise<T> {
    if (this.promise) {
      this.promise = this.promise.then(callback);
    } else {
      this.promise = callback();
    }

    const thisPromise = this.promise;
    const result = await thisPromise;
    // this.promise might have changed while we waited for thisPromise
    if (this.promise === thisPromise) {
      this.promise = undefined;
    }
    return result;
  }

  /**
   * Runs a function if no other promised function is currently running
   */
  async discardWithPromise<T>(
    callback: () => Promise<T>
  ): Promise<T | undefined> {
    if (this.promise) {
      await this.promise;
      return undefined;
    }

    this.promise = callback();

    const thisPromise = this.promise;
    const result = await thisPromise;
    if (this.promise === thisPromise) {
      this.promise = undefined;
    }
    return result;
  }

  async login(email: string, password: string): Promise<LoginResponse> {
    // Using discard as multiple logins are not allowed -
    // and if we are updating or logging out, we might not need to login again.
    const result = await this.discardWithPromise(
      this.loginInner.bind(this, email, password)
    );
    return result ?? { status: 'error', errorMessage: 'Login was discarded' };
  }

  async loginInner(email: string, password: string): Promise<LoginResponse> {
    const options = getFetchOptions();
    const fetchResult = await fetch(this.loginUrl(), {
      method: 'POST',
      ...options,
      headers: this.getHeaders(options.headers),
      body: JSON.stringify({
        user: {
          email,
          password,
        },
      }),
    });

    const result = await this.handleLoginResponse(fetchResult);
    this.rerender();
    if (!result || fetchResult.status !== 200) {
      const resultBody = await fetchResult.json().then((json) => json);
      return {
        status: 'error',
        errorMessage: resultBody.errors.detail,
      };
    }

    return result;
  }

  async loginWithToken(): Promise<LoginResponse> {
    const options = getFetchOptions();

    let fetchResult: Response;
    try {
      fetchResult = await fetch(this.loginUrl(), {
        method: 'POST',
        ...options,
        headers: this.getHeaders(options.headers),
        body: JSON.stringify({
          user: {
            token: this.required_action_token,
          },
        }),
      });
    } catch (error) {
      // Network errors during login can occur when the device is offline,
      // especially after returning from external auth (SAML SSO).
      this.logger?.warn('Login request failed due to network error:', error);
      this.setError('Network error');
      this.rerender();
      return { status: 'error', errorMessage: 'Network error' };
    }

    this.logger?.info('Login with token', fetchResult);

    const result = await this.handleLoginResponse(fetchResult);
    if (!result || fetchResult.status === 422) {
      this.setError('Invalid token');
      this.rerender();
      return { status: 'error', errorMessage: 'Invalid token' };
    }

    this.rerender();
    return result;
  }

  // eslint-disable-next-line class-methods-use-this
  loadUserProfile(): Promise<Keycloak.KeycloakProfile> {
    // @ts-expect-error // TODO: Seems to be unused
    return Promise.resolve(undefined);
  }

  logout(unused: { redirectUri?: string }): Promise<void> {
    // Waiting as we should ensure logouts actually happen
    return this.waitWithPromise(this.logoutInner.bind(this, unused));
  }

  private static async getLogoutResponseData(
    response: Response
  ): Promise<LogoutResponseData | undefined> {
    try {
      const json = await response.json();
      return json?.data as LogoutResponseData | undefined;
    } catch {
      return undefined;
    }
  }

  async logoutInner(unused: { redirectUri?: string }): Promise<void> {
    const refreshToken = await this.storage.getItem(REFRESH_TOKEN);
    if (!this.access_token && !refreshToken) {
      // Already logged out. Resetting to be sure.
      this.reset();
      return Promise.resolve(undefined);
    }

    try {
      const options = getFetchOptions();
      const logoutResponse = await fetch(this.logoutUrl(unused.redirectUri), {
        method: 'DELETE',
        ...options,
        headers: this.getHeaders(options.headers),
      });

      if (!logoutResponse.ok) {
        this.logger?.warn(
          `Logout endpoint returned status ${logoutResponse.status}`
        );
      }

      const logoutData = await PflegenaviAuthentication.getLogoutResponseData(
        logoutResponse
      );
      if (
        logoutData?.logout_type === 'saml' &&
        logoutData.saml_logout_url &&
        this.redirectIfNecessary(logoutData.saml_logout_url) !== 'redirected'
      ) {
        this.errorReporter?.captureMessage('SAML logout redirect failed', {
          tags: { source: 'auth_flow', event: 'saml_logout_redirect_failed' },
        });
      }
    } catch (error) {
      // Network errors during logout are expected when the device is offline.
      // The user will still be logged out locally via reset() in finally block.
      this.logger?.warn('Logout request failed due to network error:', error);
    } finally {
      this.reset();
      this.rerender();
    }
    return Promise.resolve(undefined);
  }

  async updateToken(refreshToken?: string): Promise<boolean> {
    // Discarding as we should only update once.
    const result: boolean | undefined = await this.discardWithPromise(
      this.updateTokenInner.bind(this, refreshToken)
    );
    if (result === undefined) {
      return false;
    }
    return result;
  }

  getTokenImmediate(): string | undefined {
    return this.access_token;
  }

  async updateTokenInner(refreshToken?: string): Promise<boolean> {
    // Wait for app to come to foreground if it's in background
    const isForeground = await this.waitForForeground();
    if (!isForeground) {
      // App stayed in background, return current token status
      return Boolean(this.access_token);
    }

    const options = getFetchOptions();
    const refresh_token =
      refreshToken ?? (await this.storage.getItem(REFRESH_TOKEN));

    if (!refresh_token) {
      this.errorReporter?.captureMessage(
        'Authentication failed: No refresh token available',
        {
          tags: { source: 'auth_flow', event: 'token_refresh_failed' },
        }
      );
      await this.logoutInner({});
      return false;
    }

    // Check network connectivity if we have a provider
    if (this.networkInfoProvider) {
      this.isNetworkConnected = await this.networkInfoProvider.isConnected();

      // If we're offline, schedule a retry and keep the user logged in
      if (!this.isNetworkConnected) {
        this.logger?.log('Network offline, deferring token refresh');
        this.clearTimer();
        this.refreshTimer = setTimeout(
          this.updateToken.bind(this, undefined),
          30000 // Try again in 30 seconds
        );
        return Boolean(this.access_token);
      }
    }

    // Check if this token was already used recently
    if (this.usedRefreshTokens.includes(refresh_token)) {
      this.logger?.log(
        'Refresh token was already used, assuming it was updated'
      );
      // Return true to indicate we still have a valid token
      return Boolean(this.access_token);
    }

    // Add this token to the used tokens list
    this.usedRefreshTokens.push(refresh_token);
    // Keep only the most recent tokens
    if (this.usedRefreshTokens.length > this.maxTrackedTokens) {
      this.usedRefreshTokens.shift(); // Remove the oldest token
    }

    try {
      const controller = new AbortController();

      const abort = setTimeout(() => controller.abort(), 30000);

      const result = await fetch(this.refreshTokenUrl(), {
        method: 'POST',
        ...options,
        headers: this.getHeaders(options.headers),
        body: JSON.stringify({
          refresh_token,
        }),
        signal: controller.signal,
      });

      clearTimeout(abort);

      // Check for SAML re-authentication response
      if (result.status === 401) {
        return await this.handleUnauthorizedResponse(result);
      }

      const response = await this.handleLoginResponse(result);
      if (
        (response && 'status' in response && response.status === 'error') ||
        result.status !== 200
      ) {
        this.errorReporter?.captureMessage(
          'Authentication failed: Token refresh request failed',
          {
            tags: { source: 'auth_flow', event: 'token_refresh_failed' },
            extra: {
              status: result.status,
              response: response ? JSON.stringify(response) : undefined,
            },
          }
        );
        await this.logoutInner({});
      }
      // Reset retry count on success
      this.retryCount = 0;
      this.rerender();
      return Boolean(response);
    } catch (error) {
      this.logger?.error('Token refresh failed due to network error:', error);

      // Allow to re-use the refresh token if the network error is due to a temporary issue
      this.usedRefreshTokens = this.usedRefreshTokens.filter(
        (token) => token !== refresh_token
      );

      // Report to Sentry if available
      if (this.errorReporter) {
        this.errorReporter.captureException(
          error instanceof Error
            ? error
            : new Error('Token refresh network error'),
          {
            tags: { source: 'auth_flow', event: 'token_refresh_network_error' },
            extra: {
              retryCount: this.retryCount,
              maxRetries: this.maxRetries,
            },
          }
        );
      }

      // Increment retry count and implement exponential backoff
      this.retryCount = Math.min(this.retryCount + 1, this.maxRetries);
      const backoffTime = Math.min(1000 * 2 ** this.retryCount, 300000); // Max 5 minutes

      // Schedule another refresh attempt with exponential backoff
      this.clearTimer();
      this.refreshTimer = setTimeout(
        this.updateToken.bind(this, undefined),
        backoffTime
      );

      // Return true to indicate we still have a valid token
      // We're just unable to refresh it at the moment
      return Boolean(this.access_token);
    }
  }

  async resetPassword(email: string): Promise<Response> {
    const options = getFetchOptions();
    const result = await fetch(this.resetPasswordUrl(), {
      method: 'POST',
      ...options,
      headers: this.getHeaders(options.headers),
      body: JSON.stringify({
        user: {
          email,
        },
      }),
    });

    return result;
  }

  async resetPasswordConfirm(
    token: string | undefined,
    body: {
      password: string;
      passwordConfirmation: string;
    }
  ): Promise<Response> {
    token = this.required_action_token || token;
    if (!token) {
      throw new Error('Missing token to confirm password reset.');
    }
    const options = getFetchOptions();
    const result = await fetch(`${this.resetPasswordUrl()}/${token}`, {
      method: 'PUT',
      ...options,
      headers: this.getHeaders(options.headers),
      body: JSON.stringify({
        user: {
          password: body.password,
          password_confirmation: body.passwordConfirmation,
        },
      }),
    });

    // If the user has required actions, we attempt to login with the token
    if (this.required_action_token) {
      await this.loginWithToken();
    }

    return result;
  }

  async acceptTermsAndConditions(): Promise<Response> {
    const token = this.required_action_token;
    if (!token) {
      throw new Error('Missing token to accept terms and conditions.');
    }
    const options = getFetchOptions();
    const result = await fetch(
      `${this.acceptTermsAndConditionsUrl()}/${token}`,
      {
        method: 'PUT',
        ...options,
        headers: this.getHeaders(options.headers),
      }
    );

    await this.loginWithToken();

    return result;
  }

  async verifyEmail(): Promise<Response> {
    const token = this.required_action_token;
    if (!token) {
      throw new Error('Missing token to verify email.');
    }
    const options = getFetchOptions();
    const result = await fetch(`${this.verifyEmailUrl()}/${token}`, {
      method: 'PUT',
      ...options,
      headers: this.getHeaders(options.headers),
    });

    this.logger?.info('Verify email result', result);

    await this.loginWithToken();

    return result;
  }

  async loginMethod(email: string): Promise<LoginMethodResponse> {
    const options = getFetchOptions();
    try {
      const result = await fetch(this.loginMethodUrl(), {
        method: 'POST',
        ...options,
        headers: this.getHeaders(options.headers),
        body: JSON.stringify({
          user_email: email,
        }),
      });

      if (result.status === 200) {
        const json = await result.json();
        return {
          loginType: json.data.login_type,
          redirectUrl: json.data.redirect_url,
        };
      }

      // Default to password login if the request fails
      return {
        loginType: 'PASSWORD',
        redirectUrl: null,
      };
    } catch (error) {
      this.logger?.error('Login method check failed:', error);
      // Default to password login on error
      return {
        loginType: 'PASSWORD',
        redirectUrl: null,
      };
    }
  }

  async samlLookup(email: string): Promise<SamlLookupResponse> {
    const options = getFetchOptions();
    try {
      const result = await fetch(this.samlLookupUrl(), {
        method: 'POST',
        ...options,
        headers: this.getHeaders(options.headers),
        body: JSON.stringify({ email }),
      });

      if (result.status === 200) {
        const json = await result.json();
        return {
          loginType: json.data.login_type,
          idpId: json.data.idp_id,
        };
      }

      return {
        loginType: 'PASSWORD',
        idpId: null,
      };
    } catch (error) {
      this.logger?.error('SAML lookup failed:', error);
      return {
        loginType: 'PASSWORD',
        idpId: null,
      };
    }
  }

  // Clean up resources when component is unmounted
  cleanup(): void {
    this.clearTimer();
    if (this.connectionListenerCleanup) {
      this.connectionListenerCleanup();
      this.connectionListenerCleanup = undefined;
    }
    if (this.appStateListenerCleanup) {
      this.appStateListenerCleanup();
      this.appStateListenerCleanup = undefined;
    }
  }
}
